import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useLocation } from 'react-router-dom';

const Timeline = () => {
  const { user } = useAuth();
  const { projectId } = useParams();
  const location = useLocation();
  const isAdminView = location.pathname.startsWith('/admin');

  const [project, setProject] = useState(null);
  const [phases, setPhases] = useState([]);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [tasks, setTasks] = useState({});
  // State for Phase Details Modal
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showPhaseModal, setShowPhaseModal] = useState(false);
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedMilestone, setSelectedMilestone] = useState(null);

  // Form states
  const [phaseForm, setPhaseForm] = useState({ name: '', description: '', start_date: '', end_date: '', status: 'pending' });
  const [milestoneForm, setMilestoneForm] = useState({ title: '', description: '', deadline: '' });
  const [taskForm, setTaskForm] = useState({ title: '', description: '', status: 'backlog' });

  useEffect(() => {
    fetchTimelineData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, projectId]);

  const fetchTimelineData = async () => {
    try {
      // Fetch project
      let projectData;
      if (isAdminView && projectId) {
        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .eq('id', projectId)
          .single();
        if (error) throw error;
        projectData = data;
      } else {
        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .eq('client_id', user.id)
          .single();
        if (error) throw error;
        projectData = data;
      }
      if (!projectData) throw new Error('No project found');
      setProject(projectData);

      // Fetch all phases
      const { data: phasesData, error: phasesError } = await supabase
        .from('timeline_phases')
        .select('*')
        .eq('project_id', projectData.id)
        .order('order_index', { ascending: true });

      if (phasesError) throw phasesError;
      setPhases(phasesData);

      // Auto-select first phase
      if (phasesData.length > 0) {
        setSelectedPhase(phasesData[0]);
        await fetchMilestones(phasesData[0].id);
      }
    } catch (error) {
      console.error('Error fetching timeline data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMilestones = async (phaseId) => {
    try {
      const { data, error } = await supabase
        .from('timeline_milestones')
        .select('*')
        .eq('phase_id', phaseId)
        .order('order_index', { ascending: true });

      if (error) throw error;
      setMilestones(data);

      // Fetch tasks for each milestone
      for (const milestone of data) {
        await fetchTasks(milestone.id);
      }
    } catch (error) {
      console.error('Error fetching milestones:', error);
    }
  };

  const fetchTasks = async (milestoneId) => {
    try {
      const { data, error } = await supabase
        .from('milestone_tasks')
        .select('*')
        .eq('milestone_id', milestoneId)
        .order('order_index', { ascending: true });

      if (error) throw error;
      setTasks(prev => ({ ...prev, [milestoneId]: data }));
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const handlePhaseClick = async (phase) => {
    setSelectedPhase(phase);
    await fetchMilestones(phase.id);
    setShowDetailsModal(true);
  };

  // Phase CRUD operations
  const handleCreatePhase = async (e) => {
    e.preventDefault();
    try {
      const maxOrder = phases.length > 0 ? Math.max(...phases.map(p => p.order_index)) : 0;

      const newPhaseData = {
        project_id: project.id,
        ...phaseForm,
        order_index: maxOrder + 1
      };

      // Sanitize dates: empty strings should be null
      if (newPhaseData.start_date === '' || !newPhaseData.start_date) newPhaseData.start_date = null;
      if (newPhaseData.end_date === '' || !newPhaseData.end_date) newPhaseData.end_date = null;

      if (newPhaseData.status === 'completed') {
        newPhaseData.progress_percentage = 100;
      }

      const { data, error } = await supabase
        .from('timeline_phases')
        .insert([newPhaseData])
        .select()
        .single();

      if (error) throw error;
      setPhases([...phases, data]);
      setShowPhaseModal(false);
      setPhaseForm({ name: '', description: '', start_date: '', end_date: '', status: 'pending' });
    } catch (error) {
      console.error('Error creating phase:', error);
      alert('Failed to create phase');
    }
  };

  const handleUpdatePhase = async (e) => {
    e.preventDefault();
    if (!editingItem) {
      alert('No phase selected');
      return;
    }
    try {
      // Build update object explicitly to handle dates properly
      const updateData = {
        name: phaseForm.name,
        description: phaseForm.description,
        status: phaseForm.status,
        start_date: phaseForm.start_date && phaseForm.start_date.trim() !== '' ? phaseForm.start_date : null,
        end_date: phaseForm.end_date && phaseForm.end_date.trim() !== '' ? phaseForm.end_date : null
      };

      // Auto-update progress based on status
      if (updateData.status === 'completed') {
        updateData.progress_percentage = 100;
      } else if (updateData.status === 'pending') {
        updateData.progress_percentage = 0;
      }

      console.log('Sending update:', updateData); // Debug log

      const { error } = await supabase
        .from('timeline_phases')
        .update(updateData)
        .eq('id', editingItem.id);

      if (error) throw error;

      setPhases(phases.map(p => p.id === editingItem.id ? { ...p, ...updateData } : p));

      // Force refresh to ensure progress calculations are consistent
      await fetchTimelineData();

      setShowPhaseModal(false);
      setEditingItem(null);
      setPhaseForm({ name: '', description: '', start_date: '', end_date: '', status: 'pending' });
    } catch (error) {
      console.error('Error updating phase:', error);
      alert('Failed to update phase: ' + (error.message || error.error_description || JSON.stringify(error)));
    }
  };

  const handleDeletePhase = async (phaseId) => {
    if (!window.confirm('Are you sure you want to delete this phase? All milestones and tasks will be deleted.')) return;

    try {
      const { error } = await supabase
        .from('timeline_phases')
        .delete()
        .eq('id', phaseId);

      if (error) throw error;

      setPhases(phases.filter(p => p.id !== phaseId));
      if (selectedPhase?.id === phaseId) {
        setSelectedPhase(null);
        setMilestones([]);
      }
    } catch (error) {
      console.error('Error deleting phase:', error);
      alert('Failed to delete phase');
    }
  };

  // Milestone CRUD operations
  const handleCreateMilestone = async (e) => {
    e.preventDefault();
    try {
      const maxOrder = milestones.length > 0 ? Math.max(...milestones.map(m => m.order_index)) : 0;
      const { data, error } = await supabase
        .from('timeline_milestones')
        .insert([{
          phase_id: selectedPhase.id,
          ...milestoneForm,
          order_index: maxOrder + 1,
          completed: false
        }])
        .select()
        .single();

      if (error) throw error;
      setMilestones([...milestones, data]);
      setShowMilestoneModal(false);
      setMilestoneForm({ title: '', description: '', deadline: '' });
    } catch (error) {
      console.error('Error creating milestone:', error);
      alert('Failed to create milestone');
    }
  };

  const handleUpdateMilestone = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase
        .from('timeline_milestones')
        .update(milestoneForm)
        .eq('id', editingItem.id);

      if (error) throw error;

      setMilestones(milestones.map(m => m.id === editingItem.id ? { ...m, ...milestoneForm } : m));
      setShowMilestoneModal(false);
      setEditingItem(null);
      setMilestoneForm({ title: '', description: '', deadline: '' });
    } catch (error) {
      console.error('Error updating milestone:', error);
      alert('Failed to update milestone');
    }
  };

  const handleDeleteMilestone = async (milestoneId) => {
    if (!window.confirm('Are you sure you want to delete this milestone? All tasks will be deleted.')) return;

    try {
      const { error } = await supabase
        .from('timeline_milestones')
        .delete()
        .eq('id', milestoneId);

      if (error) throw error;

      setMilestones(milestones.filter(m => m.id !== milestoneId));
      const newTasks = { ...tasks };
      delete newTasks[milestoneId];
      setTasks(newTasks);
    } catch (error) {
      console.error('Error deleting milestone:', error);
      alert('Failed to delete milestone');
    }
  };

  // Task CRUD operations
  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const currentTasks = tasks[selectedMilestone.id] || [];
      const maxOrder = currentTasks.length > 0 ? Math.max(...currentTasks.map(t => t.order_index)) : 0;
      const { data, error } = await supabase
        .from('milestone_tasks')
        .insert([{
          milestone_id: selectedMilestone.id,
          ...taskForm,
          order_index: maxOrder + 1
        }])
        .select()
        .single();

      if (error) throw error;

      setTasks(prev => ({
        ...prev,
        [selectedMilestone.id]: [...(prev[selectedMilestone.id] || []), data]
      }));

      setShowTaskModal(false);
      setTaskForm({ title: '', description: '', status: 'backlog' });

      // Refresh milestones to get updated progress
      await fetchMilestones(selectedPhase.id);
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Failed to create task');
    }
  };

  const handleUpdateTaskStatus = async (taskId, milestoneId, newStatus) => {
    try {
      const { error } = await supabase
        .from('milestone_tasks')
        .update({ status: newStatus })
        .eq('id', taskId);

      if (error) throw error;

      setTasks(prev => ({
        ...prev,
        [milestoneId]: prev[milestoneId].map(t =>
          t.id === taskId ? { ...t, status: newStatus } : t
        )
      }));

      // Refresh milestones to get updated progress
      await fetchMilestones(selectedPhase.id);
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId, milestoneId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      const { error } = await supabase
        .from('milestone_tasks')
        .delete()
        .eq('id', taskId);

      if (error) throw error;

      setTasks(prev => ({
        ...prev,
        [milestoneId]: prev[milestoneId].filter(t => t.id !== taskId)
      }));

      // Refresh milestones to get updated progress
      await fetchMilestones(selectedPhase.id);
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task');
    }
  };

  const getPhaseStatusColor = (status) => {
    const colors = {
      'pending': 'bg-gray-200 text-gray-600',
      'in-progress': 'bg-yellow-100 text-yellow-800',
      'completed': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-200 text-gray-600';
  };

  // eslint-disable-next-line no-unused-vars
  const getMilestoneStatusColor = (status) => {
    const colors = {
      'upcoming': 'border-gray-300 bg-white',
      'in-progress': 'border-yellow-400 bg-yellow-50',
      'completed': 'border-green-500 bg-green-50'
    };
    return colors[status] || 'border-gray-300 bg-white';
  };

  const getTaskStatusColor = (status) => {
    const colors = {
      'backlog': 'bg-gray-100 text-gray-700',
      'ready': 'bg-blue-100 text-blue-700',
      'in-progress': 'bg-yellow-100 text-yellow-700',
      'in-review': 'bg-purple-100 text-purple-700',
      'completed': 'bg-green-100 text-green-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const calculateOverallProgress = () => {
    if (phases.length === 0) return 0;
    const totalProgress = phases.reduce((sum, phase) => {
      const percentage = typeof phase.progress_percentage === 'number'
        ? phase.progress_percentage
        : parseFloat(phase.progress_percentage || 0);
      return sum + percentage;
    }, 0);
    return Math.round(totalProgress / phases.length);
  };

  const openEditPhase = (phase) => {
    setEditingItem(phase);
    setPhaseForm({
      name: phase.name,
      description: phase.description || '',
      start_date: phase.start_date || '',
      end_date: phase.end_date || '',
      status: phase.status
    });
    setShowPhaseModal(true);
  };

  const openEditMilestone = (milestone) => {
    setEditingItem(milestone);
    setMilestoneForm({
      title: milestone.title,
      description: milestone.description || '',
      deadline: milestone.deadline || ''
    });
    setShowMilestoneModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Project Timeline</h1>
            <p className="text-gray-600">Track the progress of your project across all phases.</p>
          </div>
          {isAdminView && (
            <button
              onClick={() => {
                setEditingItem(null);
                setPhaseForm({ name: '', description: '', start_date: '', end_date: '', status: 'pending' });
                setShowPhaseModal(true);
              }}
              className="px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors"
            >
              + Add Phase
            </button>
          )}
        </div>
      </motion.div>

      {/* Overall Progress */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="bg-white border border-gray-200 rounded-2xl p-6 mb-8"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Overall Progress</h2>
          <span className="text-3xl font-bold text-gray-900">{calculateOverallProgress()}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-black h-3 rounded-full transition-all duration-500"
            style={{ width: `${calculateOverallProgress()}%` }}
          />
        </div>
      </motion.div>

      {/* Phases Timeline */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="mb-8"
      >
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Project Phases</h2>
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />

          {/* Phases */}
          <div className="space-y-6">
            {phases.map((phase) => (
              <div
                key={phase.id}
                onClick={() => handlePhaseClick(phase)}
                className={`relative pl-16 cursor-pointer transition-all ${selectedPhase?.id === phase.id ? 'scale-[1.02]' : ''
                  }`}
              >
                {/* Timeline dot */}
                <div className={`absolute left-3 w-6 h-6 rounded-full border-4 ${phase.status === 'completed' ? 'bg-green-500 border-green-500' :
                  phase.status === 'in-progress' ? 'bg-yellow-400 border-yellow-400' :
                    'bg-white border-gray-300'
                  }`} />

                {/* Phase card */}
                <div className={`bg-white border-2 rounded-xl p-6 transition-all ${selectedPhase?.id === phase.id ? 'border-black shadow-lg' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-gray-900">{phase.name}</h3>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getPhaseStatusColor(phase.status)}`}>
                        {phase.status.replace('-', ' ').toUpperCase()}
                      </span>
                      {isAdminView && (
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditPhase(phase);
                            }}
                            className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg text-xs font-medium transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePhase(phase.id);
                            }}
                            className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-xs font-medium transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4">{phase.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-6 text-sm">
                      <div>
                        <span className="text-gray-600">Start: </span>
                        <span className="font-semibold text-gray-900">{formatDate(phase.start_date)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">End: </span>
                        <span className="font-semibold text-gray-900">{formatDate(phase.end_date)}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900">{phase.progress_percentage || 0}%</div>
                    </div>
                  </div>
                  {phase.progress_percentage > 0 && (
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
                      <div
                        className="bg-black h-2 rounded-full transition-all duration-300"
                        style={{ width: `${phase.progress_percentage}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Milestones for Selected Phase */}
      {/* Phase Details Modal (Milestones & Tasks) */}
      {showDetailsModal && selectedPhase && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowDetailsModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6 sticky top-0 bg-white z-10 py-2 border-b border-gray-100">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {selectedPhase.name}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">Milestones & Tasks</p>
                </div>
                <div className="flex items-center gap-3">
                  {isAdminView && (
                    <button
                      onClick={() => {
                        setEditingItem(null);
                        setMilestoneForm({ title: '', description: '', deadline: '' });
                        setShowMilestoneModal(true);
                      }}
                      className="px-4 py-2 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors text-sm"
                    >
                      + Add Milestone
                    </button>
                  )}
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {milestones.length > 0 ? (
                  milestones.map((milestone) => (
                    <div
                      key={milestone.id}
                      className="border-2 border-gray-200 rounded-xl p-4"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-bold text-gray-900">{milestone.title}</h3>
                            <span className="text-sm font-semibold text-gray-600">
                              {milestone.progress_percentage || 0}%
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{milestone.description}</p>
                          <div className="text-xs text-gray-600">
                            <span className="font-semibold">Deadline:</span> {formatDate(milestone.deadline)}
                          </div>
                          {milestone.progress_percentage > 0 && (
                            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-3">
                              <div
                                className="bg-black h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${milestone.progress_percentage}%` }}
                              />
                            </div>
                          )}
                        </div>
                        {isAdminView && (
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => {
                                setSelectedMilestone(milestone);
                                setTaskForm({ title: '', description: '', status: 'backlog' });
                                setShowTaskModal(true);
                              }}
                              className="px-3 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-xs font-medium transition-colors whitespace-nowrap"
                            >
                              + Task
                            </button>
                            <button
                              onClick={() => openEditMilestone(milestone)}
                              className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg text-xs font-medium transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteMilestone(milestone.id)}
                              className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-xs font-medium transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Tasks for this milestone */}
                      {tasks[milestone.id] && tasks[milestone.id].length > 0 && (
                        <div className="mt-4 space-y-2 bg-gray-50 rounded-lg p-3">
                          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Tasks</h4>
                          {tasks[milestone.id].map((task) => (
                            <div
                              key={task.id}
                              className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3 shadow-sm"
                            >
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">{task.title}</p>
                                {task.description && (
                                  <p className="text-xs text-gray-600 mt-1">{task.description}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 ml-4">
                                {isAdminView ? (
                                  <select
                                    value={task.status}
                                    onChange={(e) => handleUpdateTaskStatus(task.id, milestone.id, e.target.value)}
                                    className={`px-2 py-1 rounded-md text-xs font-semibold border-none focus:ring-0 cursor-pointer ${getTaskStatusColor(task.status)}`}
                                  >
                                    <option value="backlog">Backlog</option>
                                    <option value="ready">Ready</option>
                                    <option value="in-progress">In Progress</option>
                                    <option value="in-review">In Review</option>
                                    <option value="completed">Completed</option>
                                  </select>
                                ) : (
                                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getTaskStatusColor(task.status)}`}>
                                    {task.status.replace('-', ' ').toUpperCase()}
                                  </span>
                                )}
                                {isAdminView && (
                                  <button
                                    onClick={() => handleDeleteTask(task.id, milestone.id)}
                                    className="text-gray-400 hover:text-red-500 transition-colors"
                                  >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                    <p className="text-gray-500">No milestones yet for this phase.</p>
                    {isAdminView && (
                      <button
                        onClick={() => {
                          setEditingItem(null);
                          setMilestoneForm({ title: '', description: '', deadline: '' });
                          setShowMilestoneModal(true);
                        }}
                        className="mt-4 px-4 py-2 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors text-sm"
                      >
                        Create First Milestone
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}




      {/* Phase Modal */}
      <AnimatePresence>
        {showPhaseModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => {
              setShowPhaseModal(false);
              setEditingItem(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {editingItem ? 'Edit Phase' : 'Create Phase'}
              </h3>
              <form onSubmit={editingItem ? handleUpdatePhase : handleCreatePhase}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                    <input
                      type="text"
                      value={phaseForm.name}
                      onChange={(e) => setPhaseForm({ ...phaseForm, name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                    <textarea
                      value={phaseForm.description}
                      onChange={(e) => setPhaseForm({ ...phaseForm, description: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      rows="3"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date</label>
                      <input
                        type="date"
                        value={phaseForm.start_date}
                        onChange={(e) => setPhaseForm({ ...phaseForm, start_date: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">End Date</label>
                      <input
                        type="date"
                        value={phaseForm.end_date}
                        onChange={(e) => setPhaseForm({ ...phaseForm, end_date: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                    <select
                      value={phaseForm.status}
                      onChange={(e) => setPhaseForm({ ...phaseForm, status: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    >
                      <option value="pending">Pending</option>
                      <option value="in-progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPhaseModal(false);
                      setEditingItem(null);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-black hover:bg-gray-800 text-white rounded-lg font-semibold transition-colors"
                  >
                    {editingItem ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Milestone Modal */}
      <AnimatePresence>
        {showMilestoneModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => {
              setShowMilestoneModal(false);
              setEditingItem(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {editingItem ? 'Edit Milestone' : 'Create Milestone'}
              </h3>
              <form onSubmit={editingItem ? handleUpdateMilestone : handleCreateMilestone}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
                    <input
                      type="text"
                      value={milestoneForm.title}
                      onChange={(e) => setMilestoneForm({ ...milestoneForm, title: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                    <textarea
                      value={milestoneForm.description}
                      onChange={(e) => setMilestoneForm({ ...milestoneForm, description: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      rows="3"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Deadline</label>
                    <input
                      type="date"
                      value={milestoneForm.deadline}
                      onChange={(e) => setMilestoneForm({ ...milestoneForm, deadline: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowMilestoneModal(false);
                      setEditingItem(null);
                    }}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-black hover:bg-gray-800 text-white rounded-lg font-semibold transition-colors"
                  >
                    {editingItem ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Task Modal */}
      <AnimatePresence>
        {showTaskModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowTaskModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Create Task</h3>
              <form onSubmit={handleCreateTask}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
                    <input
                      type="text"
                      value={taskForm.title}
                      onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                    <textarea
                      value={taskForm.description}
                      onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                      rows="2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                    <select
                      value={taskForm.status}
                      onChange={(e) => setTaskForm({ ...taskForm, status: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                    >
                      <option value="backlog">Backlog (0%)</option>
                      <option value="ready">Ready (25%)</option>
                      <option value="in-progress">In Progress (50%)</option>
                      <option value="in-review">In Review (75%)</option>
                      <option value="completed">Completed (100%)</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowTaskModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-black hover:bg-gray-800 text-white rounded-lg font-semibold transition-colors"
                  >
                    Create
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div >
  );
};

export default Timeline;

